/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { validate } from 'app/shared/elements/ess-rule';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { ProviderService } from 'app/cd/services/provider-service';
import { CustomFieldService } from 'app/core/services/custom-field-service';
import { ICustomFieldTargetColumnsContainer } from 'app/shared/lib/custom-field-definitions';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { validateWebsiteUrl } from 'app/shared/lib/validation/validation-utilities';
import { deepClone } from 'app/shared/lib/utilities';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class SaveProviderGroup extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.toView })
    public providerGroup: CD.ProviderGroup = {};
    protected editedProviderGroup: CD.ProviderGroup = {};
    protected editIdentifier: boolean = false;
    public inModal: boolean = false;
    protected validateForm = validate;
    protected validateWebsiteUrl = validateWebsiteUrl;
    protected lookups1: ReadonlyArray<Core.CustomFieldChoice> = [];
    protected lookups2: ReadonlyArray<Core.CustomFieldChoice> = [];
    protected lookups3: ReadonlyArray<Core.CustomFieldChoice> = [];
    protected lookups4: ReadonlyArray<Core.CustomFieldChoice> = [];
    protected lookups5: ReadonlyArray<Core.CustomFieldChoice> = [];
    protected phoneNumberTypes: ReadonlyArray<CD.ProviderGroupPhoneNumberType> = [];
    protected addressTypes: ReadonlyArray<CD.ProviderGroupAddressType> = [];
    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;
    protected displayCustomLookups: boolean = false;
    protected displayCustomText: boolean = false;
    protected providerGroupBillingAddress: CD.ProviderGroupAddress = {};
    protected providerGroupPhysicalAddress: CD.ProviderGroupAddress = {};
    protected providerGroupPrimaryPhoneNumber: CD.ProviderGroupPhoneNumber = {};
    protected providerGroupFaxPhoneNumber: CD.ProviderGroupPhoneNumber = {};
    protected updateBillingAddress: boolean = false;
    protected updatePhysicalAddress: boolean = false;
    protected updatePhoneNumber: boolean = false;
    protected updateFaxNumber: boolean = false;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly providerService: ProviderService,
        private readonly customFieldService: CustomFieldService,
        private readonly toaster: Toaster,
        private readonly customFieldContext: CustomFieldContext,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.loadCustomFieldTargets();
        this.loadLookups();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        this.editedProviderGroup = {};
        this.providerGroupBillingAddress = {};
        this.providerGroupPhysicalAddress = {};
        this.providerGroupPrimaryPhoneNumber = {};
        this.providerGroupFaxPhoneNumber = {};
        this.updateBillingAddress = false;
        this.updatePhysicalAddress = false;
        this.updatePhoneNumber = false;
        this.updateFaxNumber = false;
        this.editIdentifier = false;
    }

    public async submit(): Promise<boolean> {
        return await this.updateProviderGroup();
    }

    protected async reload(): Promise<void> {
        this.loadProviderGroup();
    }

    public async loadProviderGroup() {
        if(this.providerGroup) {
            this.editedProviderGroup = deepClone(this.providerGroup);
        }
    }

    protected buildProviderGroup(): CD.ProviderGroup {
        let providerGroupBillingAddress: CD.ProviderGroupAddress = {}
        let providerGroupPhysicalAddress: CD.ProviderGroupAddress = {}
        //Per API nature, for address it is all fields or none
        //API call fails if address fields are empty or non-null. Hence the check.
        if((this.editedProviderGroup.address?.billing?.street1)
            && (this.editedProviderGroup.address?.billing?.city)
            && (this.editedProviderGroup.address?.billing?.region)
            && (this.editedProviderGroup.address?.billing?.postalCode)) {
            providerGroupBillingAddress = {
                street1: this.editedProviderGroup.address?.billing?.street1,
                street2: this.editedProviderGroup.address?.billing?.street2,
                city: this.editedProviderGroup.address?.billing?.city,
                region: { id: this.editedProviderGroup.address?.billing?.region?.id },
                postalCode: this.editedProviderGroup.address?.billing?.postalCode,
            }
        }
        if((this.editedProviderGroup.address?.physical?.street1)
            && (this.editedProviderGroup.address?.physical?.city)
            && (this.editedProviderGroup.address?.physical?.region)
            && (this.editedProviderGroup.address?.physical?.postalCode)) {
            providerGroupPhysicalAddress = {
                street1: this.editedProviderGroup.address?.physical?.street1,
                street2: this.editedProviderGroup.address?.physical?.street2,
                city: this.editedProviderGroup.address?.physical?.city,
                region: { id: this.editedProviderGroup.address?.physical?.region?.id },
                postalCode: this.editedProviderGroup.address?.physical?.postalCode,
            }
        }

        let providerGroup: CD.ProviderGroup = {
            description: this.editedProviderGroup.description,
            identifier: {
                external: this.editIdentifier ? this.editedProviderGroup.identifier?.external || null : undefined,
                nationalProvider: this.editedProviderGroup.identifier?.nationalProvider || null
            },
            emailAddress: this.editedProviderGroup.emailAddress || null,
            websiteUrl: this.editedProviderGroup.websiteUrl || null,
            address: {
                billing: providerGroupBillingAddress,
                physical: providerGroupPhysicalAddress
            },
            phoneNumber: {
                primary: {
                    number: this.editedProviderGroup.phoneNumber?.primary?.number || null,
                    extension: this.editedProviderGroup.phoneNumber?.primary?.extension || null
                },
                fax: {
                    number: this.editedProviderGroup.phoneNumber?.fax?.number || null,
                    extension: this.editedProviderGroup.phoneNumber?.fax?.extension || null
                }
            },
            custom: {
                text: {
                    value1: (this.customFieldTargetColumns?.text && this.customFieldTargetColumns.text[0].isActive) ? this.editedProviderGroup.custom?.text?.value1 || null : undefined,
                    value2: (this.customFieldTargetColumns?.text && this.customFieldTargetColumns.text[1].isActive) ? this.editedProviderGroup.custom?.text?.value2 || null : undefined,
                    value3: (this.customFieldTargetColumns?.text && this.customFieldTargetColumns.text[2].isActive) ? this.editedProviderGroup.custom?.text?.value3 || null : undefined,
                    value4: (this.customFieldTargetColumns?.text && this.customFieldTargetColumns.text[3].isActive) ? this.editedProviderGroup.custom?.text?.value4 || null : undefined,
                    value5: (this.customFieldTargetColumns?.text && this.customFieldTargetColumns.text[4].isActive) ? this.editedProviderGroup.custom?.text?.value5 || null : undefined
                },
                lookup: {
                    choice1: (this.customFieldTargetColumns?.lookup && this.customFieldTargetColumns.lookup[0].isActive) ? { id: this.editedProviderGroup.custom?.lookup?.choice1?.id } : undefined,
                    choice2: (this.customFieldTargetColumns?.lookup && this.customFieldTargetColumns.lookup[1].isActive) ? { id: this.editedProviderGroup.custom?.lookup?.choice2?.id } : undefined,
                    choice3: (this.customFieldTargetColumns?.lookup && this.customFieldTargetColumns.lookup[2].isActive) ? { id: this.editedProviderGroup.custom?.lookup?.choice3?.id } : undefined,
                    choice4: (this.customFieldTargetColumns?.lookup && this.customFieldTargetColumns.lookup[3].isActive) ? { id: this.editedProviderGroup.custom?.lookup?.choice4?.id } : undefined,
                    choice5: (this.customFieldTargetColumns?.lookup && this.customFieldTargetColumns.lookup[4].isActive) ? { id: this.editedProviderGroup.custom?.lookup?.choice5?.id } : undefined
                }
            },
        };

        return providerGroup;
    }

    protected buildUpdateProviderGroup(): CD.ProviderGroup {
        const physicalType = this.addressTypes.find(item => item.description === "Physical");
        const billingType = this.addressTypes.find(item => item.description === "Billing");
        const primaryType = this.phoneNumberTypes.find(item => item.description === "Primary");
        const faxType = this.phoneNumberTypes.find(item => item.description === "Fax");
        //Per API nature, for address it is all fields or none
        //API call fails if address fields are empty or non-null. Hence the check.
        // only time the address objs will be different but will not need updating due to deepClone results
        if(!(this.editedProviderGroup.address?.billing?.street1 === undefined && this.providerGroup.address?.billing === null)) {
            this.updateBillingAddress = this.editedProviderGroup.address?.billing?.street1 !== this.providerGroup.address?.billing?.street1
                || this.editedProviderGroup.address?.billing?.street2 !== this.providerGroup.address?.billing?.street2
                || this.editedProviderGroup.address?.billing?.city !== this.providerGroup.address?.billing?.city
                || this.editedProviderGroup.address?.billing?.region?.code !== this.providerGroup.address?.billing?.region?.code
                || this.editedProviderGroup.address?.billing?.postalCode !== this.providerGroup.address?.billing?.postalCode
            if(this.updateBillingAddress) {
                if((this.editedProviderGroup.address?.billing?.street1)
                    && (this.editedProviderGroup.address?.billing?.city)
                    && (this.editedProviderGroup.address?.billing?.region)
                    && (this.editedProviderGroup.address?.billing?.postalCode)) {
                    this.providerGroupBillingAddress = {
                        id: this.editedProviderGroup.address?.billing?.id,
                        street1: this.editedProviderGroup.address?.billing?.street1,
                        street2: this.editedProviderGroup.address?.billing?.street2 === "" ? null : this.editedProviderGroup.address?.billing?.street2,
                        city: this.editedProviderGroup.address?.billing?.city,
                        region: { id: this.editedProviderGroup.address?.billing?.region?.id },
                        postalCode: this.editedProviderGroup.address?.billing?.postalCode,
                        type: { id: billingType?.id },
                        group: { id: this.editedProviderGroup.id }
                    }
                } else if(this.editedProviderGroup.address?.billing?.id) {
                    this.providerGroupBillingAddress = {
                        id: this.editedProviderGroup.address?.billing?.id
                    }
                }
            }
        }
        // only time the address objs will be different but will not need updating due to deepClone results
        if(!(this.editedProviderGroup.address?.physical?.street1 === undefined && this.providerGroup.address?.physical === null)) {
            this.updatePhysicalAddress = this.editedProviderGroup.address?.physical?.street1 !== this.providerGroup.address?.physical?.street1
                || this.editedProviderGroup.address?.physical?.street2 !== this.providerGroup.address?.physical?.street2
                || this.editedProviderGroup.address?.physical?.city !== this.providerGroup.address?.physical?.city
                || this.editedProviderGroup.address?.physical?.region?.code !== this.providerGroup.address?.physical?.region?.code
                || this.editedProviderGroup.address?.physical?.postalCode !== this.providerGroup.address?.physical?.postalCode
            if(this.updatePhysicalAddress) {
                if((this.editedProviderGroup.address?.physical?.street1)
                    && (this.editedProviderGroup.address?.physical?.city)
                    && (this.editedProviderGroup.address?.physical?.region)
                    && (this.editedProviderGroup.address?.physical?.postalCode)) {
                    this.providerGroupPhysicalAddress = {
                        id: this.editedProviderGroup.address?.physical?.id,
                        street1: this.editedProviderGroup.address?.physical?.street1,
                        street2: this.editedProviderGroup.address?.physical?.street2 === "" ? null : this.editedProviderGroup.address?.physical?.street2,
                        city: this.editedProviderGroup.address?.physical?.city,
                        region: { id: this.editedProviderGroup.address?.physical?.region?.id },
                        postalCode: this.editedProviderGroup.address?.physical?.postalCode,
                        type: { id: physicalType?.id },
                        group: { id: this.editedProviderGroup.id }
                    }
                } else if(this.editedProviderGroup.address?.physical?.id) {
                    this.providerGroupPhysicalAddress = {
                        id: this.editedProviderGroup.address?.physical?.id
                    }
                }
            }
        }
        // only time the phonenumber objs will be different but will not need updating due to deepClone results
        if(!(this.editedProviderGroup.phoneNumber?.primary?.number === undefined && this.providerGroup.phoneNumber?.primary === null)) {
            this.updatePhoneNumber = this.editedProviderGroup.phoneNumber?.primary?.number !== this.providerGroup.phoneNumber?.primary?.number
                || this.editedProviderGroup.phoneNumber?.primary?.extension !== this.providerGroup.phoneNumber?.primary?.extension
            if(this.editedProviderGroup.phoneNumber?.primary?.number) {
                this.providerGroupPrimaryPhoneNumber = {
                    id: this.editedProviderGroup.phoneNumber?.primary?.id,
                    number: this.editedProviderGroup.phoneNumber?.primary?.number || null,
                    extension: this.editedProviderGroup.phoneNumber?.primary?.extension || null,
                    type: { id: primaryType?.id },
                    group: { id: this.editedProviderGroup.id }
                }
            } else if(this.editedProviderGroup.phoneNumber?.primary?.id) {
                this.providerGroupPrimaryPhoneNumber = {
                    id: this.editedProviderGroup.phoneNumber?.primary?.id,
                }
            }
        }
        // only time the phonenumber objs will be different but will not need updating due to deepClone results
        if(!(this.editedProviderGroup.phoneNumber?.fax?.number === undefined && this.providerGroup.phoneNumber?.fax === null)) {
            this.updateFaxNumber = this.editedProviderGroup.phoneNumber?.fax?.number !== this.providerGroup.phoneNumber?.fax?.number
                || this.editedProviderGroup.phoneNumber?.fax?.extension !== this.providerGroup.phoneNumber?.fax?.extension
            if(this.editedProviderGroup.phoneNumber?.fax?.number) {
                this.providerGroupFaxPhoneNumber = {
                    id: this.editedProviderGroup.phoneNumber?.fax?.id,
                    number: this.editedProviderGroup.phoneNumber?.fax?.number || null,
                    extension: this.editedProviderGroup.phoneNumber?.fax?.extension || null,
                    type: { id: faxType?.id },
                    group: { id: this.editedProviderGroup.id }
                }
            } else if(this.editedProviderGroup.phoneNumber?.fax?.id) {
                this.providerGroupFaxPhoneNumber = {
                    id: this.editedProviderGroup.phoneNumber?.fax?.id,
                }
            }
        }

        let providerGroup: CD.ProviderGroup = {
            id: this.editedProviderGroup.id,
            description: this.editedProviderGroup.description,
            identifier: {
                external: this.editIdentifier ? this.editedProviderGroup.identifier?.external || null : undefined,
                nationalProvider: this.editedProviderGroup.identifier?.nationalProvider || null
            },
            emailAddress: this.editedProviderGroup.emailAddress || null,
            websiteUrl: this.editedProviderGroup.websiteUrl || null,
            address: {
                billing: this.providerGroupBillingAddress,
                physical: this.providerGroupPhysicalAddress
            },
            phoneNumber: {
                primary: this.providerGroupPrimaryPhoneNumber,
                fax: this.providerGroupFaxPhoneNumber
            },
            custom: {
                text: {
                    value1: (this.customFieldTargetColumns?.text && this.customFieldTargetColumns.text[0].isActive) ? this.editedProviderGroup.custom?.text?.value1 || null : undefined,
                    value2: (this.customFieldTargetColumns?.text && this.customFieldTargetColumns.text[1].isActive) ? this.editedProviderGroup.custom?.text?.value2 || null : undefined,
                    value3: (this.customFieldTargetColumns?.text && this.customFieldTargetColumns.text[2].isActive) ? this.editedProviderGroup.custom?.text?.value3 || null : undefined,
                    value4: (this.customFieldTargetColumns?.text && this.customFieldTargetColumns.text[3].isActive) ? this.editedProviderGroup.custom?.text?.value4 || null : undefined,
                    value5: (this.customFieldTargetColumns?.text && this.customFieldTargetColumns.text[4].isActive) ? this.editedProviderGroup.custom?.text?.value5 || null : undefined
                },
                lookup: {
                    choice1: (this.customFieldTargetColumns?.lookup && this.customFieldTargetColumns.lookup[0].isActive) ? { id: this.editedProviderGroup.custom?.lookup?.choice1?.id } : undefined,
                    choice2: (this.customFieldTargetColumns?.lookup && this.customFieldTargetColumns.lookup[1].isActive) ? { id: this.editedProviderGroup.custom?.lookup?.choice2?.id } : undefined,
                    choice3: (this.customFieldTargetColumns?.lookup && this.customFieldTargetColumns.lookup[2].isActive) ? { id: this.editedProviderGroup.custom?.lookup?.choice3?.id } : undefined,
                    choice4: (this.customFieldTargetColumns?.lookup && this.customFieldTargetColumns.lookup[3].isActive) ? { id: this.editedProviderGroup.custom?.lookup?.choice4?.id } : undefined,
                    choice5: (this.customFieldTargetColumns?.lookup && this.customFieldTargetColumns.lookup[4].isActive) ? { id: this.editedProviderGroup.custom?.lookup?.choice5?.id } : undefined
                }
            },
        };

        return providerGroup;
    }

    protected clear(): void {
        this.providerGroup = {};
        this.editedProviderGroup = {};
        this.providerGroupBillingAddress = {};
        this.providerGroupPhysicalAddress = {};
        this.providerGroupPrimaryPhoneNumber = {};
        this.providerGroupFaxPhoneNumber = {};
    }

    private async loadCustomFieldTargets(): Promise<void> {
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.ProviderGroup');

        const customLookups: Core.CustomFieldChoice[] = this.customFieldTargetColumns.lookup?.filter(lookup => lookup.isActive) || [];
        const customTexts: Core.CustomFieldChoice[] = this.customFieldTargetColumns.text?.filter(text => text.isActive) || [];

        this.displayCustomLookups = customLookups.length > 0;
        this.displayCustomText = customTexts.length > 0;

    }

    private async housekeepProviderGroupAddress(address: CD.ProviderGroupAddress): Promise<void> {
        if(!address.id && address.street1) {
            //it could be insert or reactivate
            //query db for inactive address // if exists, reactivate // only then we update
            if(address.group?.id && address.type?.id) {
                const includes: string[] = [];
                const options: StandardLoadOptions = {
                    includeInactive: true
                };
                const fields = ['id'];
                const addressToReactivate = await this.providerService.loadAddressByProviderGroup(address.group?.id, address.type?.id, options, includes, fields)
                if(addressToReactivate.results.length > 0 && addressToReactivate.results[0].id) {
                    // reactivate
                    await this.providerService.activateProviderGroupAddress(addressToReactivate.results[0].id, true)
                    address.id = addressToReactivate.results[0].id;
                }
            }

        }
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const lookupIncludes = [
                'definition.target'
            ];

            const [lookups1, lookups2, lookups3, lookups4, lookups5, phoneNumberTypes, addressTypes] = await Promise.all(
                [
                    this.customFieldService.loadCustomFieldChoicesByDefinition('CD.ProviderGroup', 'lookup.choice1', {}, lookupIncludes),
                    this.customFieldService.loadCustomFieldChoicesByDefinition('CD.ProviderGroup', 'lookup.choice2', {}, lookupIncludes),
                    this.customFieldService.loadCustomFieldChoicesByDefinition('CD.ProviderGroup', 'lookup.choice3', {}, lookupIncludes),
                    this.customFieldService.loadCustomFieldChoicesByDefinition('CD.ProviderGroup', 'lookup.choice4', {}, lookupIncludes),
                    this.customFieldService.loadCustomFieldChoicesByDefinition('CD.ProviderGroup', 'lookup.choice5', {}, lookupIncludes),
                    this.providerService.loadProviderGroupPhoneNumberTypes(),
                    this.providerService.loadProviderGroupAddressTypes()
                ]);

            this.lookups1 = lookups1.results;
            this.lookups2 = lookups2.results;
            this.lookups3 = lookups3.results;
            this.lookups4 = lookups4.results;
            this.lookups5 = lookups5.results;

            this.phoneNumberTypes = phoneNumberTypes.results;
            this.addressTypes = addressTypes.results;

        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveProviderGroup(): Promise<boolean> {
        if(!this.providerGroup) return false;
        let isSuccessful = false;
        let errorMessage: string = "";
        try {
            this.state.beginSave();
            const response = await this.providerService.saveProviderGroup(this.buildProviderGroup());
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            if(error.description) { errorMessage = error.description; }
            throw error;
        }
        finally {
            if(isSuccessful) { this.clear(); }
            this.toaster.standard.saved('cd:providerGroup.$', isSuccessful);
            if(!isSuccessful) {
                const exception = (this.toaster.toast())
                    .title('Detail')
                    .class('ess-toast-error')
                    .icon('ui:icons.error')
                    .message(errorMessage)
                exception.show();
            }
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    private async updateProviderGroup(): Promise<boolean> {
        if(!this.providerGroup) return false;
        let isSuccessful = false;
        const providerGroup = this.buildUpdateProviderGroup();
        let errorMessage: string = "";
        try {
            this.state.beginSave();
            await this.housekeepProviderGroupAddress(providerGroup.address?.billing!);
            await this.housekeepProviderGroupAddress(providerGroup.address?.physical!);
            const [providerGroupResponse, billingAddressResponse, physicalAddressResponse, primaryPhoneNumberResponse, faxPhoneNumberResponse] = await Promise.all([
                this.providerService.saveProviderGroup(providerGroup),
                this.updateBillingAddress ? this.providerService.handleProviderGroupAddress(providerGroup.address?.billing!) : null,
                this.updatePhysicalAddress ? this.providerService.handleProviderGroupAddress(providerGroup.address?.physical!) : null,
                this.updatePhoneNumber ? this.providerService.handleProviderGroupPhoneNumber(providerGroup.phoneNumber?.primary!) : null,
                this.updateFaxNumber ? this.providerService.handleProviderGroupPhoneNumber(providerGroup.phoneNumber?.fax!) : null
            ])
            isSuccessful = (providerGroupResponse.isSuccessful && (!this.updatePhysicalAddress ? true : physicalAddressResponse?.isSuccessful) && (!this.updateBillingAddress ? true : billingAddressResponse?.isSuccessful) && (!this.updatePhoneNumber ? true : primaryPhoneNumberResponse?.isSuccessful) && (!this.updateFaxNumber ? true : faxPhoneNumberResponse?.isSuccessful)) || false;
        }
        catch(error) {
            if(error.description) { errorMessage = error.description; }
            throw error;
        }
        finally {
            if(isSuccessful) { this.clear(); }
            this.toaster.standard.saved('cd:providerGroup.$', isSuccessful);
            if(!isSuccessful) {
                const exception = (this.toaster.toast())
                    .title('Detail')
                    .class('ess-toast-error')
                    .icon('ui:icons.error')
                    .message(errorMessage)
                exception.show();
            }
            this.state.endSave();
            return isSuccessful;
        }
    }

}
